import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { UserService } from '../service/user.service';
import { TokenRefreshService } from '../service/token-refresh.service';

// Auth endpoints must never be retried/refreshed (they issue tokens or are the refresh
// call itself) — a 401 here means bad credentials / dead session, not an expired access token.
const AUTH_ENDPOINTS = [
  '/api/Auth/ValidateUser',
  '/api/Auth/validateUserRedirection',
  '/api/Auth/validateUserRedirectionNew',
  '/api/Auth/login',
  '/api/Auth/Refresh',
];

// Backends that accept the MAIN JWT access token — the token override + refresh apply here.
// The automation backend (labourAutomationApiUrl) is intentionally NOT listed: some of its
// calls use a separate token, so overriding Authorization there would break them.
// blobUrl (Azure, SAS-authenticated) is also intentionally excluded.
const COVERED_BASES = [
  environment.apiUrl,
  (environment as any).scopingApiUrl,
  (environment as any).labourDocUrl,
].filter((b: any): b is string => Boolean(b));

/**
 * Keeps every request carrying the freshest access token and transparently refreshes
 * it on a 401, then retries the original request — so the 5-minute access-token expiry
 * is invisible to the user and to all ~98 services (no per-service change needed).
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  // Guard the backends that validate the main JWT (main API + billing + reports/blob).
  if (!COVERED_BASES.some((base) => req.url.startsWith(base))) {
    return next(req);
  }

  const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => req.url.includes(p));
  const userService = inject(UserService);
  const refresher = inject(TokenRefreshService);

  // Attach the current access token from sessionStorage. This also neutralises the
  // codebase's "token captured in the constructor" pattern: after a refresh a service
  // still holds the old token, but this override sends the current one regardless.
  let authReq = req;
  if (!isAuthEndpoint) {
    const token = userService.getAccessToken();
    if (token) {
      authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only a 401 on a protected endpoint is a candidate for a token refresh.
      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      return refresher.refreshToken().pipe(
        // If the refresh ITSELF fails (refresh token missing/expired/reused) → log out.
        catchError((refreshError) => {
          refresher.forceLogout();
          return throwError(() => refreshError);
        }),
        // Refresh succeeded → retry the original request once with the new token.
        switchMap((newToken) =>
          next(authReq.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
        )
      );
    })
  );
};
